<template>
    <div class="circle-page">
        <!-- ========== 智能圈选 ========== -->
        <div class="page-title">智能圈选 <small>客户分群 · 运营场景自助圈人</small></div>
        <div class="seg-layout">
            <section class="panel dim-panel">
                <div class="panel-head"><div class="panel-title">圈选维度</div></div>
                <el-input v-model="dimKw" class="dim-search" placeholder="搜索维度…" clearable @input="onDimSearchInput" />
                <div class="dim-tree" v-html="dimTreeHtml" @click="onDimTreeClick"></div>
            </section>
            <section class="panel cond-panel">
                <div class="panel-head">
                    <div class="panel-title">圈选条件 <span class="cond-count">（{{ segState.conds.length }}）</span></div>
                </div>
                <div class="cond-scroll">
                    <div v-for="(c, i) in segState.conds" :key="c.key + '-' + i" class="cond-row">
                        <div class="cond-head">
                            <span class="cond-layer" :style="{ background: layerColor(c) + '22', color: layerColor(c) }">{{ c.layer }}</span>
                            <span class="cond-name">{{ c.name }}</span>
                            <span v-if="c.desc" class="cond-desc" :title="c.desc">{{ c.desc }}</span>
                            <span class="cond-remove" @click="removeCond(i)">×</span>
                        </div>
                        <div class="cond-body">
                            <!-- 组合维度：所属主体 · 添加人（坐席），选主体联动展示该主体下坐席 -->
                            <template v-if="c.key === 'subject_agent'">
                                <div class="sa-wrap">
                                    <div class="sa-subs">
                                        <span class="sa-label">所属主体</span>
                                        <el-button v-for="opt in c.opts" :key="opt.value" size="small" class="enum-pill" :class="{ on: c.subjects.indexOf(opt.value) >= 0 }" @click="toggleSubjectAgent(c, opt.value)">{{ opt.label }}</el-button>
                                        <span class="cond-tip">可多选</span>
                                    </div>
                                    <div v-for="sid in c.subjects" :key="sid" class="sa-seat-row">
                                        <span class="sa-label">{{ subjLabel(sid) }}</span>
                                        <el-button size="small" class="enum-pill" :class="{ on: isSeatAll(c, sid) }" @click="setSeatAll(c, sid)">全部坐席</el-button>
                                        <el-button v-for="s in (c.seatOpts[sid] || [])" :key="s.value" size="small" class="enum-pill" :class="{ on: isSeatOn(c, sid, s.value) }" @click="toggleSeat(c, sid, s.value)">{{ s.label }}</el-button>
                                    </div>
                                    <div class="sa-hint">不勾坐席 ＝ 该主体下的全部坐席；多个主体之间是「或」的关系</div>
                                </div>
                            </template>
                            <!-- 枚举 / 布尔 / 地域 -->
                            <template v-else-if="c.kind === 'enum'">
                                <span class="cond-op">属于</span>
                                <!-- 地域：先选「多选省 / 多选市」再选择具体省市 -->
                                <template v-if="c.ui === 'select'">
                                    <el-select v-model="c.regionMode" class="cond-op-sel" style="width: 96px" @change="onRegionModeChange(c)">
                                        <el-option v-for="lv in c.multiLevel" :key="lv" :label="REGION_LEVEL_LABEL[lv] || lv" :value="lv" />
                                    </el-select>
                                    <template v-if="c.regionMode === 'city'">
                                        <el-cascader v-model="c.cityCodes" class="cond-cascader" :options="regionCityTree" :props="regionCascaderProps" collapse-tags placeholder="请选择市（可多选）" clearable style="width: 320px" />
                                    </template>
                                    <template v-else>
                                        <el-select v-model="c.val" multiple collapse-tags placeholder="请选择省（可多选）" style="width: 260px">
                                            <el-option v-for="opt in c.opts" :key="opt.value" :label="opt.label" :value="opt.value" />
                                        </el-select>
                                    </template>
                                </template>
                                <!-- 其他枚举：pill 单选（默认） / 多选（ui:multi，带可多选提示） -->
                                <template v-else>
                                    <div class="cond-enum">
                                        <el-button v-for="opt in c.opts" :key="opt.value" size="small" class="enum-pill" :class="{ on: c.val.indexOf(opt.value) >= 0 }" @click="toggleEnumVal(c, opt.value)">{{ opt.label }}</el-button>
                                    </div>
                                    <span v-if="c.ui === 'multi'" class="cond-tip">可多选</span>
                                </template>
                            </template>
                            <!-- 添加时间（date）：运算符（>= / <=）+ 天数 / 指定自然月 -->
                            <template v-else-if="c.kind === 'time' && c.ui === 'time'">
                                <div class="cond-enum">
                                    <el-button size="small" class="enum-pill" :class="{ on: c.timeMode !== 'month' }" @click="setTimeMode(c, 'days')">最近 N 天内</el-button>
                                    <el-button size="small" class="enum-pill" :class="{ on: c.timeMode === 'month' }" @click="setTimeMode(c, 'month')">指定自然月</el-button>
                                </div>
                                <template v-if="c.timeMode === 'month'">
                                    <el-select v-model="c.months" multiple collapse-tags placeholder="请选择自然月（可多选）" style="width: 260px">
                                        <el-option-group v-for="g in monthGroups" :key="g.year" :label="g.year">
                                            <el-option v-for="o in g.items" :key="o" :label="o" :value="o" />
                                        </el-option-group>
                                    </el-select>
                                </template>
                                <template v-else>
                                    <el-select v-model="c.op" class="cond-op-sel" style="width: 76px" @change="resetEst">
                                        <el-option v-for="o in timeDayOps(c)" :key="o.value" :label="o.label" :value="o.value" />
                                    </el-select>
                                    <el-input v-model="c.days" class="cond-input-sm" placeholder="天数" @input="onlyPositiveInt(c, 'days')"  clearable />
                                    <span class="cond-unit">≤ 取最近N天内添加;≥ 取N天前及更早添加</span>
                                </template>
                            </template>
                            <!-- 数值（int） -->
                            <template v-else-if="c.kind === 'num' || c.kind === 'time'">
                                <el-select v-model="c.op" class="cond-op-sel" style="width: 76px" @change="onNumOpChange(c)">
                                    <el-option v-for="o in opOptionsFor(c)" :key="o.value" :label="o.label" :value="o.value" />
                                </el-select>
                                <template v-if="c.op === 'between'">
                                    <el-input v-model="c.min" class="cond-input-sm" placeholder="最小" @input="onlyPositiveInt(c, 'min')"  clearable />
                                    <span class="cond-unit">—</span>
                                    <el-input v-model="c.max" class="cond-input-sm" placeholder="最大" @input="onlyPositiveInt(c, 'max')"  clearable />
                                </template>
                                <template v-else>
                                    <el-input v-model="c.val" class="cond-input" placeholder="数值" @input="onlyPositiveInt(c, 'val')"  clearable />
                                </template>
                                <span class="cond-unit">{{ unitFor(c) }}</span>
                            </template>
                            <!-- 店铺下单（行为 + 时间模式）：最近 N 天内 / 指定自然月 + 运算符 + 次数 -->
                            <template v-else-if="c.key === 'submit_order'">
                                <div class="cond-enum">
                                    <el-button size="small" class="enum-pill" :class="{ on: c.timeMode !== 'month' }" @click="setOrderTimeMode(c, 'days')">最近 N 天内</el-button>
                                    <el-button size="small" class="enum-pill" :class="{ on: c.timeMode === 'month' }" @click="setOrderTimeMode(c, 'month')">指定自然月</el-button>
                                </div>
                                <template v-if="c.timeMode === 'month'">
                                    <el-select v-model="c.months" multiple collapse-tags placeholder="请选择自然月（可多选）" style="width: 260px">
                                        <el-option-group v-for="g in monthGroups" :key="g.year" :label="g.year">
                                            <el-option v-for="o in g.items" :key="o" :label="o" :value="o" />
                                        </el-option-group>
                                    </el-select>
                                </template>
                                <template v-else>
                                    <span class="cond-tip">最近</span>
                                    <el-input v-model="c.days" class="cond-input-sm" placeholder="天数" @input="onlyPositiveInt(c, 'days')"  clearable />
                                    <span class="cond-tip">天内</span>
                                </template>
                                <el-select v-model="c.op" class="cond-op-sel" style="width: 76px" @change="resetEst">
                                    <el-option v-for="o in opOptionsFor(c)" :key="o.value" :label="o.label" :value="o.value" />
                                </el-select>
                                <template v-if="c.op !== 'none'">
                                    <el-input v-model="c.val" class="cond-input-sm" placeholder="次数" @input="onlyPositiveInt(c, 'val')"  clearable />
                                    <span class="cond-unit">次</span>
                                </template>
                            </template>
                            <!-- 行为 -->
                            <template v-else>
                                <span class="cond-tip">最近</span>
                                <el-input v-model="c.days" class="cond-input-sm" placeholder="天数" @input="onlyPositiveInt(c, 'days')"  clearable />
                                <span class="cond-unit">天</span>
                                <span class="cond-tip">内</span>
                                <el-select v-model="c.op" class="cond-op-sel" style="width: 76px" @change="resetEst">
                                    <el-option v-for="o in opOptionsFor(c)" :key="o.value" :label="o.label" :value="o.value" />
                                </el-select>
                                <template v-if="c.op !== 'none'">
                                    <el-input v-model="c.val" class="cond-input-sm" placeholder="次数" @input="onlyPositiveInt(c, 'val')"  clearable />
                                    <span class="cond-unit">次</span>
                                </template>
                            </template>
                        </div>
                    </div>
                    <div v-show="!segState.conds.length" class="cond-empty">从左侧选择维度，开始圈选客户</div>
                </div>
                <!-- 计算人群数量模块（暂隐藏，后续需要时放开） -->
                <!--
                <div class="count-banner">
                    <el-button class="btn-primary block" @click="computeEstimate">计算人群数量</el-button>
                    <div class="cb-label" style="margin-top:14px">预估命中人数</div>
                    <div class="cb-value">{{ estCount }}</div>
                    <div class="cb-sub">{{ estSub }}</div>
                </div>
                -->
            </section>
        </div>
        <div class="seg-floatbar">
            <el-button class="btn-ghost" @click="goBack">取消</el-button>
            <el-button class="btn-primary" @click="openSaveDialog">保存人群</el-button>
        </div>

        <!-- 保存人群弹窗 -->
        <el-dialog v-model="saveDialog" title="保存人群" width="380px" :show-close="false" align-center overlay-class="save-dialog-overlay" class="save-dialog">
            <div class="seg-form">
                <label>人群名称</label>
                <el-input v-model="segName" placeholder="如：山东未激活设备客户"  clearable />
                <label>人群分类</label>
                <el-input v-model="segCat" placeholder="如：设备激活催办"  clearable />
            </div>
            <template #footer>
                <el-button class="btn-ghost" @click="saveDialog = false">取消</el-button>
                <el-button class="btn-primary" :loading="saving" @click="onSegConfirm">保存</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
//导入接口（渠道列表 / 主体列表 / 成员列表 / 维度元数据 / 创建人群）
import { newgethmqdlist, newgetcomp2, newgethmcylist, newgetcirclekey, newcreatecircle } from '@/assets/js/common.js'

//路由跳转
const router = useRouter()
//取消：返回人群管理
const goBack = () => { router.push('/navkh/circlepeopledata'); }

/* ============== 工具 ============== */
function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
function fmtInt(v) { return Math.round(v).toLocaleString('en-US'); }
//圈选条件数值输入框：仅允许正整数（禁 0、负数、小数、中英文、特殊字符、空格等）
const onlyPositiveInt = (c, key) => {
    c[key] = String(c[key] == null ? '' : c[key]).replace(/[^\d]/g, '').replace(/^0+/, '');
};

/* ============== 状态 ============== */
var TOTAL = 128600;
var segState = reactive({ conds: [] });
const dimKw = ref('')
const dimTreeHtml = ref('')
const estCount = ref('—')
const estSub = ref('添加条件后点击「计算人群数量」获取结果')

/* ============== 本地接口选项（渠道 / 成员 / 主体，source: local） ============== */
var channelOpts = reactive([])   // 添加渠道：newgethmqdlist → {value:id, label:title}
var memberOpts = reactive([])    // 添加人（坐席）：newgethmcylist → {value:internal_user_id, label:wx_name}
var corpOpts = reactive([])      // 所属主体：newgetcomp2 → {value:id, label:corp_name}
var LOCAL_OPTS = { add_channel: channelOpts, add_user: memberOpts, corp_id: corpOpts };

/* ============== 地域数据（省市，取自 tongyidiqu.js，回传后台仍为文字省/市） ============== */
//多选省：flat 省列表 [{value:'北京市',label:'北京市'},...]
const regionProvinceOpts = ref([])
//多选市：二级联动（省 → 市），叶子 value 用市编码、label 用市名称
const regionCityTree = ref([])
//市编码 → 市名称（用于回传后台时把编码转回文字）
var regionCityLabel = {}
//el-cascader 配置：多选 + 只返回叶子（市编码）
const regionCascaderProps = { multiple: true, emitPath: false }
//地域层级 → 界面文案（由接口 multi_level 驱动，province=省 / city=市）
var REGION_LEVEL_LABEL = { province: '省', city: '市' };
//加载省市数据（本地 tongyidiqu.js 三级：省 → 市 → 区；仅取省、市两级）
async function loadRegionData() {
    try {
        var res = await fetch('https://file.storep.91haoka.cn/tongyidiqu.js')
        var tree = await res.json()
        regionProvinceOpts.value = (tree || []).map(function (p) {
            return { value: p.label, label: p.label }
        })
        regionCityTree.value = (tree || []).map(function (p) {
            var children = (p.children || []).map(function (c) {
                regionCityLabel[c.value] = c.label
                return { value: c.value, label: c.label }
            })
            return { value: p.label, label: p.label, children: children }
        })
    } catch (err) {
        console.log('地域数据加载异常：', err)
    }
}
//地域「多选省/多选市」切换：清空已选值
const onRegionModeChange = (c) => { c.val = []; c.cityCodes = []; }

/* ============== 维度配置（由 getCircleKey 返回数据构建） ============== */
var DIMS = reactive([])
var LAYER_COLORS = { '属性': '#4F46E5', '客户行为': '#7C3AED', '浏览行为': '#0EA5E9', '其他': '#94A3B8' }
//各维度 UI 形态（沿用演示确认的交互）：pill=单选 / multi=多选 / select=省市多选 / time=最近N天内+指定自然月
var KEY_UI = {
    gender: 'pill', blacklist: 'pill', aligned: 'pill', corp_id: 'pill',
    add_channel: 'multi', add_user: 'multi', region: 'select', add_time: 'time',
    subject_agent: 'subject_agent'
}
//各维度描述（与演示稿保持一致；接口未返回 desc 时兜底展示，key 兼容演示稿与接口键名）
var DESC_MAP = {
    //属性层
    gender: '客户登记性别',
    age: '客户年龄',
    add_time: '被添加为好友时间',
    add_channel: '客户来源渠道',
    subject: '企微账号主体', corp_id: '企微账号主体',
    subject_agent: '企微账号主体及其接待坐席',
    add_agent: '企微的接待坐席', add_user: '企微的接待坐席',
    aligned: '是否已对齐业务系统（知道在业务端他是谁）',
    region: '客户所在省市',
    blacklist: '是否被列入黑名单',
    //客户行为层（演示稿键名）
    shop_visit: '打开店铺页面',
    shop_order: '在店铺完成下单',
    shop_cart: '加入购物车',
    shop_share: '分享店铺或商品',
    pv: '浏览商品详情',
    search: '搜索商品',
    favorite: '收藏商品',
    buy_cnt: '累计下单笔数',
    buy_amt: '累计消费金额',
    avg_price: '平均客单价',
    repurchase_cycle: '两次购买间隔天数',
    //客户行为层（接口键名）
    shop_view: '打开店铺页面',
    browse: '浏览商品详情',
    add_cart: '加入购物车',
    share: '分享店铺或商品',
    submit_order: '在店铺完成下单',
    pay_order: '完成支付',
    claim_coupon: '领取优惠券'
}
//API 运算符 → 界面文案
var NUM_OP_LABEL = { 'gte': '>=', 'lte': '<=', 'gt': '>', 'lt': '<', 'eq': '=', 'between': '区间' };
var BEH_OP_LABEL = { 'gte': '≥', 'lte': '≤', 'gt': '>', 'lt': '<', 'eq': '=', 'none': '未发生' };
function opLabelFor(kind, op) {
    if (kind === 'behavior') return BEH_OP_LABEL[op] || op;
    if (kind === 'num' || kind === 'time') return NUM_OP_LABEL[op] || op;
    return '属于';
}
//API kind → 界面 kind
function mapKind(k) {
    if (k === 'bool' || k === 'cascader') return 'enum';
    if (k === 'int') return 'num';
    if (k === 'date') return 'time';
    if (k === 'behavior') return 'behavior';
    return 'enum';
}
//默认选中的 API 运算符
function defaultOpFor(kind, ops) {
    ops = ops || [];
    if (kind === 'num') return ops.indexOf('gte') >= 0 ? 'gte' : (ops[0] || 'gte');
    if (kind === 'time') return 'gte';
    if (kind === 'behavior') return ops.indexOf('gte') >= 0 ? 'gte' : (ops[0] || 'gte');
    return ops.indexOf('in') >= 0 ? 'in' : (ops[0] || 'in');
}
//地域（cascader）选项展平：省 + 市
function flattenOptions(options) {
    var flat = [];
    (options || []).forEach(function (o) {
        flat.push({ value: o.value, label: o.label });
        (o.children || []).forEach(function (ch) { flat.push({ value: ch.value, label: ch.label }); });
    });
    return flat;
}
//维度 kind → 维度树上的类型标签（组合维度 subject_agent 显示「组合」）
function kindTag(it) {
    if (it.key === 'subject_agent') return '组合';
    if (it.kind === 'enum') return '枚举';
    if (it.kind === 'num') return '数值';
    if (it.kind === 'time') return '时间';
    return '行为';
}
//根据 getCircleKey 返回的分组动态构建 DIMS
function buildDims(raw) {
    DIMS.splice(0, DIMS.length);
    (raw || []).forEach(function (g) {
        var layer = g.name || '其他';
        var items = (g.items || []).map(function (it) {
            var kind = mapKind(it.kind);
            var ui = kind === 'time' ? 'time' : (KEY_UI[it.key] || 'pill');
            var opts = [];
            //本地接口维度（添加渠道/所属主体/添加人）优先用本地接口选项填充；其余取接口 options
            if (LOCAL_OPTS[it.key]) opts = LOCAL_OPTS[it.key];
            else if (it.key === 'region') opts = regionProvinceOpts.value;   //地域改用 tongyidiqu.js 的省列表（多选省）
            else if (it.options && it.options.length) opts = flattenOptions(it.options);
            return {
                key: it.key, name: it.name, desc: it.desc || DESC_MAP[it.key] || '',
                kind: kind, ui: ui,
                source: it.source || 'rocky',
                apiOps: it.ops || [],
                op: defaultOpFor(kind, it.ops),
                opts: opts,
                windowDays: it.window_days || null,
                multiLevel: it.multi_level || ['province', 'city'],
                cfg: it
            };
        });
        DIMS.push({ layer: layer, short: layer, color: LAYER_COLORS[layer] || '#4F46E5', bg: 'rgba(79,70,229,.10)', items: items });
    });
}

/* ============== 维度树渲染（接口数据返回后调用） ============== */
function renderDims(q) {
    q = (q || '').trim().toLowerCase();
    var addedKeys = segState.conds.map(function (c) { return c.key; });
    var html = DIMS.map(function (layer) {
        var items = (layer.items || []).filter(function (it) {
            var n = String(it.name || '');
            return !q || n.toLowerCase().indexOf(q) >= 0 || String(layer.layer || '').toLowerCase().indexOf(q) >= 0;
        });
        if (!items.length) return '';
        var ih = items.map(function (it) {
            var added = addedKeys.indexOf(it.key) >= 0;
            return '<div class="dim-item' + (added ? ' added' : '') + '" data-key="' + it.key + '">'
                + '<span class="dim-dot" style="background:' + layer.color + '"></span>'
                + '<span class="dim-name">' + esc(it.name || '') + '</span>'
                + '<span class="dim-kind">' + kindTag(it) + '</span>'
                + (added ? '<span class="dim-added-tag">✓ 已添加</span>' : '<span class="dim-add">＋</span>')
                + '</div>';
        }).join('');
        return '<div class="dim-layer"><div class="dim-layer-title">' + esc(layer.layer || '') + '</div>' + ih + '</div>';
    }).join('');
    dimTreeHtml.value = html || '<div class="cond-empty">无匹配维度</div>';
}
//DIMS 数据变化（接口返回后）自动重渲染
watch(DIMS, function () { renderDims(dimKw.value); }, { deep: true });
//搜索输入
const onDimSearchInput = () => { renderDims(dimKw.value); }
function findDim(key) {
    var found = null;
    DIMS.forEach(function (l) { l.items.forEach(function (it) { if (it.key === key) found = { layer: l, item: it }; }); });
    return found;
}
//维度点击添加（已添加的维度不可重复添加）
const onDimTreeClick = (e) => {
    var it = e.target.closest('.dim-item'); if (!it || it.classList.contains('added')) return;
    addCondition(it.dataset.key);
}
function addCondition(key) {
    var fd = findDim(key); if (!fd) return;
    var it = fd.item;
    var cond = {
        key: key, layer: fd.layer.layer, name: it.name, desc: it.desc || '',
        kind: it.kind, ui: it.ui || 'pill', source: it.source || 'rocky',
        apiOps: it.apiOps || [], op: it.op,
        opts: it.opts || [], val: [], min: '', max: '',
        days: '30', months: [], timeMode: 'days',
        regionMode: (it.multiLevel && it.multiLevel[0]) || 'province', cityCodes: [],
        multiLevel: it.multiLevel || ['province', 'city'],
        cfg: it.cfg || null
    };
    if (it.kind === 'enum') { cond.val = (it.ui === 'select' || it.ui === 'multi') ? [] : ((it.opts || []).length ? [it.opts[0].value] : []); }
    else if (it.kind === 'num') { cond.val = ''; }
    else if (it.kind === 'behavior') { cond.val = '1'; }
    //组合维度「所属主体 · 添加人（坐席）」：主体多选 + 按主体联动坐席
    if (it.key === 'subject_agent') {
        cond.subjects = [];      // 已选主体 value（corp id）
        cond.seatOpts = {};      // corp_id -> [{value: internal_user_id, label: wx_name}] 该主体下坐席
        cond.seatVal = {};       // corp_id -> [已选坐席 value]（seatAll=true 时为空）
        cond.seatAll = {};       // corp_id -> bool 是否勾选「全部坐席」（默认 true）
        cond.opts = corpOpts.slice();  // 主体 pill 直接复用主体列表
    }
    segState.conds.push(cond);
    renderDims(dimKw.value);
    resetEst();
}
//删除条件
const removeCond = (i) => { segState.conds.splice(i, 1); renderDims(dimKw.value); resetEst(); }

/* ============== 圈选条件渲染辅助 ============== */
//条件所属维度颜色
function layerColor(c) { return (DIMS.filter(function (l) { return l.layer === c.layer; })[0] || { color: '#94A3B8' }).color; }
//运算符下拉选项
function opOptionsFor(c) {
    return (c.apiOps || []).map(function (op) { return { value: op, label: opLabelFor(c.kind, op) }; });
}
//添加时间「按天数」运算符下拉：仅保留 >= / <=，过滤掉 between 等其他运算符
function timeDayOps(c) {
    var allowed = { gte: 1, lte: 1 };
    return (c.apiOps || []).filter(function (op) { return allowed[op]; })
        .map(function (op) { return { value: op, label: opLabelFor(c.kind, op) }; });
}
//枚举点选：ui:multi 可多选切换；默认单选（点选即替换，只保留一个）
const toggleEnumVal = (c, v) => {
    if (c.ui === 'multi') {
        var idx = c.val.indexOf(v);
        if (idx >= 0) c.val.splice(idx, 1); else c.val.push(v);
    } else {
        c.val = [v];
    }
}
/* ============== 组合维度「所属主体 · 添加人（坐席）」交互 ============== */
//主体切换：勾选则联动加载该主体下坐席；取消则移除该主体及其坐席
const toggleSubjectAgent = (c, sid) => {
    var i = c.subjects.indexOf(sid);
    if (i >= 0) {
        c.subjects.splice(i, 1);
        delete c.seatOpts[sid];
        delete c.seatVal[sid];
        delete c.seatAll[sid];
    } else {
        c.subjects.push(sid);
        c.seatAll[sid] = true;   // 默认「全部坐席」
        c.seatVal[sid] = [];
        if (!c.seatOpts[sid]) c.seatOpts[sid] = [];
    }
    resetEst();
    loadSeatOptionsForSubjects(c);
}
//主体 value -> 展示名（坐席行左侧主体标签）
const subjLabel = (sid) => {
    var f = (corpOpts || []).filter(function (o) { return o.value === sid; })[0];
    return f ? f.label : String(sid);
}
//是否该主体勾选「全部坐席」
const isSeatAll = (c, sid) => c.seatAll[sid] !== false;
//勾选「全部坐席」：清空该主体具体坐席
const setSeatAll = (c, sid) => { c.seatAll[sid] = true; c.seatVal[sid] = []; resetEst(); }
//具体坐席点选：选坐席即退出「全部坐席」，同一主体内可多选；取消「全部坐席」且清空坐席时按全部坐席处理
const toggleSeat = (c, sid, v) => {
    if (c.seatAll[sid] === true) { c.seatAll[sid] = false; c.seatVal[sid] = []; }
    var arr = c.seatVal[sid] || (c.seatVal[sid] = []);
    var i = arr.indexOf(v);
    if (i >= 0) arr.splice(i, 1); else arr.push(v);
    resetEst();
}
const isSeatOn = (c, sid, v) => (c.seatAll[sid] !== true) && ((c.seatVal[sid] || []).indexOf(v) >= 0);
//按当前已选主体加载坐席：复用设备列表接口（newgethmcylist）。
//接口 corp_id 为单值过滤，多主体时逐个请求，把坐席合并到对应主体下
const loadSeatOptionsForSubjects = (c) => {
    var ids = (c.subjects || []).slice();
    if (!ids.length) return;
    ids.forEach(function (sid) {
        newgethmcylist({ page: 1, page_size: 20000, is_wecom_disabled: 0, corp_id: sid }).then((response) => {
            if (response.data.msg.code != 0) return;
            var list = response.data.data.list.data || [];
            var arr = c.seatOpts[sid] || (c.seatOpts[sid] = []);
            arr.splice(0, arr.length);
            list.forEach(function (i) {
                if (i.corp_id == null || !i.wx_name) return;
                var v = i.internal_user_id || i.id;
                if (arr.some(function (o) { return o.value === v; })) return;
                arr.push({ value: v, label: i.wx_name });
            });
        })
    })
}
//添加时间：最近 N 天内 / 指定自然月 切换
const setTimeMode = (c, mode) => { c.timeMode = mode; c.op = mode === 'month' ? 'between' : 'gte'; }
//店铺下单：时间模式切换（只改 timeMode，保留行为运算符 op）
const setOrderTimeMode = (c, mode) => { c.timeMode = mode; }
//数值运算符切换
const onNumOpChange = (c) => { if (c.op !== 'between') { c.min = ''; c.max = ''; } resetEst(); }
//数值单位
function unitFor(c) {
    if (c.kind === 'time') return '天前';
    return '';
}
//最近 36 个自然月（按年分组）
const monthGroups = computed(() => {
    var now = new Date(), y = now.getFullYear(), m = now.getMonth();
    var groups = [], cur = null;
    for (var k = 0; k < 36; k++) {
        var yy = y, mm = m - k;
        while (mm < 0) { mm += 12; yy--; }
        var yl = yy + '年';
        if (!cur || cur.year !== yl) { cur = { year: yl, items: [] }; groups.push(cur); }
        cur.items.push(yy + '年' + (mm + 1) + '月');
    }
    return groups;
});

/* ============== 人数估算 ============== */
function resetEst() { estCount.value = '—'; estSub.value = '添加条件后点击「计算人群数量」获取结果'; }
var RATE = {
    gender: 0.50, age: 0.45, add_time: 0.55, add_channel: 0.40, corp_id: 0.60, add_user: 0.30,
    region: 0.42, blacklist: 0.05, aligned: 0.72,
    click: 0.78, favorite: 0.33, share: 0.25, submit_order: 0.66, pay_order: 0.60, claim_coupon: 0.40
};
function computeEstimate() {
    function f(c) { return (c.kind === 'behavior' && c.op === 'none') ? (1 - (RATE[c.key] || 0.5)) : (RATE[c.key] || 0.5); }
    var rate = segState.conds.reduce(function (p, c) { return p * f(c); }, 1);
    setEst(Math.max(0, Math.round(TOTAL * rate)));
}
function setEst(n) {
    estCount.value = fmtInt(n);
    estSub.value = '占全量客户 ' + (TOTAL ? (n / TOTAL * 100).toFixed(1) : 0) + '% · 预估可触达人数';
}

/* ============== 保存人群：构建提交数据 ============== */
//指定自然月 → 并集日期区间 [start, end]
function monthRange(months) {
    if (!months || !months.length) return [];
    var start = null, end = null;
    months.forEach(function (m) {
        var mm = /(\d+)年(\d+)月/.exec(m);
        if (!mm) return;
        var y = parseInt(mm[1], 10), mo = parseInt(mm[2], 10);
        var s = new Date(y, mo - 1, 1);
        var e = new Date(y, mo, 0);
        if (!start || s < start) start = s;
        if (!end || e > end) end = e;
    });
    if (!start) return [];
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    return [
        start.getFullYear() + '-' + pad(start.getMonth() + 1) + '-01',
        end.getFullYear() + '-' + pad(end.getMonth() + 1) + '-' + pad(end.getDate())
    ];
}
//组合维度「所属主体 · 添加人（坐席）」→ 拆分提交为 corp_id + add_user 两个 attr（后端计算不变）。
//语义：多个主体之间是「或」（corp_id in 全选主体）；主体内不勾坐席 = 全部坐席；
//若全部主体都是全部坐席则只提交 corp_id；混选坐席时把各主体的坐席（含全部坐席主体）展开求并集提交 add_user。
function buildSubjectAgentAttrs(c) {
    var subjects = (c.subjects || []).slice();
    if (!subjects.length) return [];
    var allAll = subjects.every(function (sid) { return c.seatAll[sid] !== false || !(c.seatVal[sid] || []).length; });
    var attrs = [{ key: 'corp_id', op: 'in', values: subjects }];
    if (!allAll) {
        var seatIds = [];
        subjects.forEach(function (sid) {
            var specific = (c.seatAll[sid] !== false) ? [] : (c.seatVal[sid] || []);
            var expandAll = !specific.length;
            if (expandAll) {
                (c.seatOpts[sid] || []).forEach(function (o) { if (seatIds.indexOf(o.value) < 0) seatIds.push(o.value); });
            } else {
                specific.forEach(function (v) { if (seatIds.indexOf(v) < 0) seatIds.push(v); });
            }
        });
        if (seatIds.length) attrs.push({ key: 'add_user', op: 'in', values: seatIds });
    }
    return attrs;
}
//根据当前圈选条件构建 attrs / behaviors
function buildConditions() {
    var attrs = [], behaviors = [];
    segState.conds.forEach(function (c) {
        //组合维度「所属主体 · 添加人（坐席）」：拆分提交
        if (c.key === 'subject_agent') {
            buildSubjectAgentAttrs(c).forEach(function (a) { attrs.push(a); });
            return;
        }
        if (c.kind === 'behavior') {
            var beh = {
                action_type: c.key,
                op: c.op,
                window_days: Math.min(365, Math.max(1, parseInt(c.days, 10) || 30))
            };
            //「未发生」无次数；其余运算符带 count
            if (c.op !== 'none') {
                beh.count = parseInt(c.val, 10) || 0;
            }
            behaviors.push(beh);
            return;
        }
        var item = { key: c.key, op: c.op, values: [] };
        if (c.kind === 'enum') {
            if (c.key === 'region' && c.regionMode === 'city') {
                //地域·多选市：把市编码回传成文字市名称
                item.values = (c.cityCodes || []).map(function (code) { return regionCityLabel[code] || code; });
            } else {
                item.values = c.val.slice();
            }
        } else if (c.kind === 'num') {
            if (c.op === 'between') {
                item.values = [parseFloat(c.min) || 0, parseFloat(c.max) || 0];
            } else {
                item.values = [parseFloat(c.val) || 0];
            }
        } else if (c.kind === 'time') {
            if (c.timeMode === 'month') {
                item.op = 'between';
                item.values = monthRange(c.months);
            } else {
                item.op = c.op || 'gte';
                item.values = [parseInt(c.days, 10) || 0];
            }
        }
        attrs.push(item);
    });
    return { attrs: attrs, behaviors: behaviors };
}
//校验圈选条件是否都已填值（返回错误提示，null 表示通过）
function validateConditions() {
    for (var i = 0; i < segState.conds.length; i++) {
        var c = segState.conds[i];
        var name = c.name || '';
        if (c.key === 'subject_agent') {
            if (!c.subjects || !c.subjects.length) {
                return '请为「' + name + '」选择主体';
            }
        } else if (c.kind === 'enum') {
            //枚举：单选/多选/省市下拉都必须有选中值（地域·多选市校验 cityCodes）
            var enumVal = (c.key === 'region' && c.regionMode === 'city') ? c.cityCodes : c.val;
            if (!enumVal || !enumVal.length) {
                return '请为「' + name + '」选择值';
            }
        } else if (c.kind === 'num') {
            if (c.op === 'between') {
                if (c.min === '' || c.min == null || c.max === '' || c.max == null) {
                    return '请为「' + name + '」填写完整区间值';
                }
            } else if (c.val === '' || c.val == null) {
                return '请为「' + name + '」填写数值';
            }
        } else if (c.kind === 'time') {
            if (c.timeMode === 'month') {
                if (!c.months || !c.months.length) {
                    return '请为「' + name + '」选择自然月';
                }
            } else if (c.days === '' || c.days == null) {
                return '请为「' + name + '」填写天数';
            }
        } else if (c.kind === 'behavior') {
            //店铺下单·指定自然月：校验自然月；其余校验天数
            if (c.key === 'submit_order' && c.timeMode === 'month') {
                if (!c.months || !c.months.length) {
                    return '请为「' + name + '」选择自然月';
                }
            } else if (c.days === '' || c.days == null) {
                return '请为「' + name + '」填写天数';
            }
            if (c.op !== 'none' && (c.val === '' || c.val == null)) {
                return '请为「' + name + '」填写次数';
            }
        }
    }
    return null;
}
const saveDialog = ref(false)
//人群名称 / 人群分类：默认空，由用户填写
const segName = ref('')
const segCat = ref('')
const saving = ref(false)
//点击「保存人群」按钮：先校验圈选条件都已填值，通过才打开弹窗
const openSaveDialog = () => {
    if (!segState.conds.length) { ElMessage.warning('请先添加圈选条件'); return; }
    var err = validateConditions();
    if (err) { ElMessage.warning(err); return; }
    saveDialog.value = true;
}
const onSegConfirm = () => {
    var nm = (segName.value || '').trim(), ct = (segCat.value || '').trim();
    if (!nm || !ct) { ElMessage.error('人群名称和人群分类不能为空'); return; }
    if (saving.value) return;
    var conditions = buildConditions();
    var submitData = {
        name: nm,
        category: ct,
        conditions: {
            logic: 'AND',
            attrs: conditions.attrs,
            behavior_logic: 'AND',
            behaviors: conditions.behaviors
        }
    };
    saving.value = true;
    newcreatecircle(submitData).then((response) => {
        saving.value = false;
        if (response.data.msg.code == 0) {
            saveDialog.value = false;
            segName.value = '';
            segCat.value = '';
            ElMessage({ message: '人群保存成功：' + nm, type: 'success' });
            //保存成功返回人群管理页面
            router.push('/navkh/circlepeopledata');
        }
    }).catch(() => {
        saving.value = false;
        ElMessage.error('保存失败，请稍后重试');
    })
}

/* ============== 接口请求：渠道 / 成员 / 主体 / 维度元数据 ============== */
//渠道列表（newgethmqdlist）：label 用 title，value 用 id
const getChannelList = () => {
    newgethmqdlist({ page: 1, page_size: 200000, channel_title: '', channel_type_id: '' }).then((response) => {
        if (response.data.msg.code == 0) {
            channelOpts.splice(0, channelOpts.length);
            (response.data.data.data || []).forEach(function (i) { if (i.title) channelOpts.push({ value: i.id, label: i.title }); });
        }
    })
}
//主体列表（newgetcomp2）：label 用 corp_name，value 用 id
const getCorpList = () => {
    newgetcomp2().then((response) => {
        if (response.data.msg.code == 0) {
            corpOpts.splice(0, corpOpts.length);
            (response.data.data || []).forEach(function (i) { if (i.corp_name) corpOpts.push({ value: i.id, label: i.corp_name }); });
        }
    })
}
//维度元数据（newgetcirclekey）：接口返回 {msg, data:{groups:[...]}}，取 groups 构建维度并渲染
const getCircleKey = () => {
    newgetcirclekey().then((response) => {
        var body = response.data;
        var groups = body && body.data && body.data.groups ? body.data.groups : null;
        if (groups && groups.length) {
            buildDims(groups);
            renderDims(dimKw.value);
        }
    }).catch(function (err) {
        console.log('dimensions 请求异常：', err);
    })
}

/* ============== 初始化 ============== */
onMounted(async () => {
    //先加载地域省市数据（tongyidiqu.js），再拉取维度元数据构建维度
    await loadRegionData();
    //等接口数据返回后由 getCircleKey 动态构建维度并渲染
    getCircleKey();
    getChannelList();
    getCorpList();
});
</script>

<style scoped>
.circle-page {
    /* 一屏高度（减去顶部导航、底部页脚、内容区上下内边距），整页 flex 布局让底部悬浮栏固定可见 */
    height: calc(100vh - 128px);
    display: flex;
    flex-direction: column;
    --bg: #F7F8FC;
    --card: #ffffff;
    --primary: #4F46E5;
    --primary2: #7C3AED;
    --primarySoft: #A5B4FC;
    --red: #EF4444;
    --green: #10B981;
    --amber: #F59E0B;
    --text: #1E293B;
    --muted: #64748B;
    --grid: #EEF1F6;
    --border: #E8ECF3;
    --shadow: 0 1px 3px rgba(15, 23, 42, .06), 0 10px 30px rgba(79, 70, 229, .06);
    --radius: 14px;
}

.container { max-width: none; margin: 0; padding: 0 0 20px; }
.page-title { font-size: 21px; font-weight: 800; margin: 4px 0 18px; }
.page-title small { font-weight: 500; color: var(--muted); font-size: 14px; margin-left: 10px; }
.panel { background: var(--card); border-radius: var(--radius); box-shadow: var(--shadow); padding: 18px; }
.panel-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 10px; flex-wrap: wrap; }
.panel-title { font-size: 16px; font-weight: 700; }

/* 按钮 */
.btn-primary { background: linear-gradient(90deg, var(--primary), var(--primary2)); color: #fff; border: 0; border-radius: 10px; padding: 9px 18px; font-weight: 700; cursor: pointer; font-size: 14px; }
.btn-primary:hover { opacity: .92; }
.btn-ghost { background: #F1F3F9; color: var(--text); border: 0; border-radius: 10px; padding: 9px 18px; font-weight: 600; cursor: pointer; font-size: 14px; }
.btn-ghost:hover { background: #E7EAF2; }
.btn-primary:focus-visible, .btn-ghost:focus-visible { outline: none; }

/* 圈选布局 */
.block { width: 100%; margin-top: 8px; }
.seg-floatbar { display: flex; justify-content: center; align-items: center; gap: 12px; margin-top: 16px; padding: 16px 0; background: var(--card); border-radius: var(--radius); box-shadow: var(--shadow); flex-shrink: 0; }
.seg-layout { flex: 1 1 auto; min-height: 0; display: grid; grid-template-columns: 440px 1fr; gap: 16px; align-items: stretch; }
.seg-layout > .panel { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
.dim-tree { flex: 1 1 auto; min-height: 0; overflow-y: auto; margin: 2px -6px 0; padding: 2px 6px 6px; }
.cond-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; margin-top: 2px; }
.count-banner { flex-shrink: 0; }
.dim-tree::-webkit-scrollbar, .cond-scroll::-webkit-scrollbar { width: 8px; }
.dim-tree::-webkit-scrollbar-thumb, .cond-scroll::-webkit-scrollbar-thumb { background: rgba(100, 116, 139, .35); border-radius: 8px; }
.dim-tree::-webkit-scrollbar-track, .cond-scroll::-webkit-scrollbar-track { background: transparent; }

/* 搜索框 */
.dim-search { width: 100%; margin-bottom: 12px; flex-shrink: 0; }
.dim-search :deep(.el-input__wrapper) {
    border-radius: 10px;
    box-shadow: 0 0 0 1px var(--border) inset;
    background: #fff;
    padding: 1px 12px;
}
.dim-search :deep(.el-input__wrapper.is-focus) { box-shadow: 0 0 0 1px var(--primary) inset; }
.dim-search :deep(.el-input__inner) { height: 36px; color: var(--text); font-size: 14px; }

/* 维度列表（v-html 注入，需用 :deep 命中） */
.dim-tree :deep(.dim-layer-title) { font-size: 12px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--muted); margin: 14px 0 6px; }
.dim-tree :deep(.dim-layer) { padding: 2px 0; }
.dim-tree :deep(.dim-item) { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; cursor: pointer; font-size: 14px; color: var(--text); margin: 2px 0; }
.dim-tree :deep(.dim-dot) { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.dim-tree :deep(.dim-name) { flex: 1; }
.dim-tree :deep(.dim-kind) { font-size: 11px; padding: 2px 7px; border-radius: 999px; font-weight: 700; background: #F1F3F9; color: var(--muted); }
.dim-tree :deep(.dim-add) { color: var(--primary); font-weight: 700; font-size: 18px; opacity: 0; transition: opacity .12s; margin-left: auto; }
.dim-tree :deep(.dim-item:hover) { background: #F1F3F9; }
.dim-tree :deep(.dim-item:hover .dim-add) { opacity: 1; }
.dim-tree :deep(.dim-item.added) { background: #F0FDF4; color: #15803D; cursor: default; }
.dim-tree :deep(.dim-item.added:hover) { background: #F0FDF4; }
.dim-tree :deep(.dim-item.added .dim-name) { color: #15803D; }
.dim-tree :deep(.dim-item.added .dim-kind) { background: rgba(21, 128, 61, .12); color: #15803D; }
.dim-tree :deep(.dim-added-tag) { margin-left: auto; font-size: 12px; font-weight: 700; color: #16A34A; background: rgba(22, 163, 74, .12); padding: 2px 9px; border-radius: 999px; white-space: nowrap; }
.dim-tree :deep(.cond-empty) { text-align: center; color: var(--muted); font-size: 14px; padding: 34px 0; }

/* 圈选条件 */
.cond-count { color: var(--muted); font-weight: 500; font-size: 13.5px; }
.cond-row { border: 1px solid var(--border); border-radius: 12px; padding: 12px 14px; margin-bottom: 10px; position: relative; background: #fff; }
.cond-head { display: flex; align-items: center; gap: 8px; margin-bottom: 9px; }
.cond-layer { font-size: 11px; padding: 2px 7px; border-radius: 999px; font-weight: 700; }
.cond-name { font-weight: 700; font-size: 14.5px; }
.cond-op { color: var(--muted); font-size: 13.5px; }
.cond-remove { margin-left: auto; color: var(--muted); cursor: pointer; border: 0; background: transparent; font-size: 18px; line-height: 1; }
.cond-remove:hover { color: var(--red); }
.cond-body { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cond-enum { display: flex; flex-wrap: wrap; gap: 8px; }
/* 组合维度：所属主体 · 添加人（坐席） */
.sa-wrap { width: 100%; display: flex; flex-direction: column; gap: 8px; }
.sa-subs { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sa-seat-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; border-top: 1px dashed var(--border); padding-top: 8px; }
.sa-label { font-size: 12.5px; font-weight: 700; color: var(--muted); margin-right: 2px; white-space: nowrap; }
.sa-hint { color: var(--muted); font-size: 12px; }
.enum-pill { height: auto; border: 1px solid var(--border); background: #fff; border-radius: 8px; padding: 6px 12px; font-size: 14px; color: var(--text); line-height: 1.4; margin-left: 0 !important; }
.enum-pill:hover { background: #fff; color: var(--text); }
.enum-pill.on { background: var(--primary); color: #fff; border-color: var(--primary); }
.enum-pill.on:hover { background: var(--primary); color: #fff; }
.cond-op-sel { width: 76px; }
.cond-op-sel :deep(.el-select__wrapper) { border-radius: 8px; box-shadow: 0 0 0 1px var(--border) inset; padding: 0 8px; font-size: 13.5px; }
.cond-op-sel :deep(.el-select__wrapper.is-focused) { box-shadow: 0 0 0 1px var(--primary) inset; }
.cond-input { width: 120px; }
.cond-input-sm { width: 78px; }
.cond-input :deep(.el-input__wrapper), .cond-input-sm :deep(.el-input__wrapper) {
    border-radius: 8px;
    box-shadow: 0 0 0 1px var(--border) inset;
    background: #fff;
}
.cond-input :deep(.el-input__wrapper.is-focus), .cond-input-sm :deep(.el-input__wrapper.is-focus) { box-shadow: 0 0 0 1px var(--primary) inset; }
.cond-input :deep(.el-input__inner), .cond-input-sm :deep(.el-input__inner) { font-size: 14px; color: var(--text); }
.cond-cascader :deep(.el-cascader__tags) { flex-wrap: nowrap; }
.cond-cascader :deep(.el-input__wrapper) { border-radius: 8px; box-shadow: 0 0 0 1px var(--border) inset; background: #fff; }
.cond-cascader :deep(.el-input__wrapper.is-focus) { box-shadow: 0 0 0 1px var(--primary) inset; }
.cond-cascader :deep(.el-input__inner) { font-size: 14px; color: var(--text); }
.cond-unit { color: var(--muted); font-size: 13px; }
.cond-tip { color: var(--muted); margin: 0 4px; white-space: nowrap; }
.cond-desc { color: #94A3B8; font-size: 13px; font-weight: 400; margin-left: 6px; white-space: nowrap; cursor: default; }
.cond-empty { text-align: center; color: var(--muted); font-size: 14px; padding: 34px 0; }

/* 人数估算 */
.count-banner { margin-top: 14px; background: linear-gradient(90deg, rgba(79, 70, 229, .07), rgba(124, 58, 237, .04)); border: 1px solid rgba(79, 70, 229, .16); border-radius: 12px; padding: 16px 18px; text-align: center; }
.cb-label { color: var(--muted); font-size: 13.5px; font-weight: 600; }
.cb-value { font-size: 35px; font-weight: 800; color: var(--primary); margin: 4px 0; }
.cb-sub { color: var(--muted); font-size: 13px; }

@media (max-width: 1180px) {
    .circle-page { height: auto; }
    .seg-layout { grid-template-columns: 1fr; height: auto; min-height: auto; }
    .seg-layout > .panel { overflow: visible; }
    .dim-tree, .cond-scroll { overflow: visible; }
}
</style>

<style>
/* ============== 保存人群弹窗（对齐演示稿 modal 样式；el-dialog 挂载到 body，需全局样式） ============== */
.save-dialog-overlay { background: rgba(15, 23, 42, .4); }
.save-dialog {
    border-radius: 14px;
    padding: 22px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, .06), 0 10px 30px rgba(79, 70, 229, .06);
}
.save-dialog .el-dialog__header { padding: 0; margin: 0 0 14px; }
.save-dialog .el-dialog__title { font-size: 17px; font-weight: 700; color: #1E293B; }
.save-dialog .el-dialog__body { padding: 0; }
.save-dialog .el-dialog__footer { padding: 0; margin-top: 18px; display: flex; gap: 10px; }
.save-dialog .el-dialog__footer .el-button { flex: 1; margin-left: 0; }
.seg-form label { font-size: 13.5px; color: #64748B; font-weight: 600; display: block; margin: 12px 0 5px; }
.save-dialog .el-input__wrapper { border-radius: 10px; box-shadow: 0 0 0 1px #E8ECF3 inset; background: #fff; padding: 8px 12px; min-height: 40px; }
.save-dialog .el-input__wrapper.is-focus { box-shadow: 0 0 0 1px #4F46E5 inset; }
.save-dialog .el-input__inner { font-size: 14px; color: #1E293B; }
</style>
